import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import logo from "../assets/cablenxt.png";
import clientLogo from "../assets/logo.png";

import cable1 from "../assets/login/cable1.jpg";
import cable2 from "../assets/login/cable2.png";
import cable3 from "../assets/login/cable3.jpg";
import cable4 from "../assets/login/cable4.jpg";
import cable5 from "../assets/login/cable5.jpg";

const backgroundImages = [
  cable1,
  cable2,
  cable3,
  cable4,
  cable5,
];

/* PASSWORD INPUT WITH SHOW / HIDE TOGGLE */
const PasswordInput = ({ value, onChange, placeholder }) => {
  const [visible, setVisible] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <input
        type={visible ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{ ...styles.input, paddingRight: "48px" }}
      />

      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        title={visible ? "Hide password" : "Show password"}
        style={styles.eyeButton}
      >
        {visible ? (
          /* EYE OFF */
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          /* EYE */
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
};

const Login = () => {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] =
    useState("");
  const [loading, setLoading] =
    useState(false);

  const [currentImage, setCurrentImage] =
    useState(0);

  /* FORGOT PASSWORD FLOW */
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState("userId"); // "userId" | "otp" | "reset" | "done"
  const [forgotUserId, setForgotUserId] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const navigate = useNavigate();

  /* IMAGE SLIDER */
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) =>
        prev === backgroundImages.length - 1
          ? 0
          : prev + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleLogin = async () => {
    if (!loginId || !password) {
      alert(
        "Please enter your user ID and password"
      );
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(
                  "https://master.erp.sbeecables.com/api/auth/login",

        {
          email: loginId, // backend field name; value is the User ID now, not an email
          password,
        }
      );

      localStorage.setItem(
        "token",
        res.data.token
      );

      localStorage.setItem(
        "user",
        JSON.stringify(res.data)
      );

      navigate("/home");
    } catch (err) {
      console.error(err);
      alert("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  /* RESET THE FORGOT-PASSWORD MODAL BACK TO ITS INITIAL STATE */
  const closeForgotModal = () => {
    setShowForgotModal(false);
    setForgotStep("userId");
    setForgotUserId("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setForgotError("");
    setForgotLoading(false);
  };

  /* STEP 1: SEND OTP TO THE ACCOUNT'S REGISTERED EMAIL */
  const handleSendOtp = async () => {
    setForgotError("");

    if (!forgotUserId) {
      setForgotError("Please enter your User ID");
      return;
    }

    try {
      setForgotLoading(true);

      await axios.post(
        "https://master.erp.sbeecables.com/api/auth/forgot-password",
        { userId: forgotUserId }
      );

      setForgotStep("otp");
    } catch (err) {
      console.error(err);
      setForgotError(
        err.response?.data?.message ||
          "Could not send OTP. Please check your User ID and try again."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  /* STEP 2: VERIFY OTP */
  const handleVerifyOtp = async () => {
    setForgotError("");

    if (!otp) {
      setForgotError("Please enter the OTP sent to your email");
      return;
    }

    try {
      setForgotLoading(true);

      await axios.post(
        "https://master.erp.sbeecables.com/api/auth/verify-otp",
        { userId: forgotUserId, otp }
      );

      setForgotStep("reset");
    } catch (err) {
      console.error(err);
      setForgotError(
        err.response?.data?.message ||
          "Invalid or expired OTP. Please try again."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  /* STEP 3: SET NEW PASSWORD */
  const handleResetPassword = async () => {
    setForgotError("");

    if (!newPassword || !confirmPassword) {
      setForgotError("Please enter and confirm your new password");
      return;
    }

    if (newPassword.length < 6) {
      setForgotError("Password must be at least 6 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setForgotError("Passwords do not match");
      return;
    }

    try {
      setForgotLoading(true);

      await axios.post(
        "https://master.erp.sbeecables.com/api/auth/reset-password-otp",
        { userId: forgotUserId, otp, newPassword }
      );

      setForgotStep("done");
    } catch (err) {
      console.error(err);
      setForgotError(
        err.response?.data?.message ||
          "Could not reset password. Please try again."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <>
      {/* GOOGLE FONT */}
      <link
        href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <div style={styles.page}>
        {/* LEFT PANEL */}
        <div
          style={{
            ...styles.leftPanel,
            backgroundImage: `url(${backgroundImages[currentImage]})`,
          }}
        >
          {/* OVERLAY */}
          <div style={styles.overlay}></div>

          {/* OPTIONAL TEXT */}
          <div style={styles.leftContent}>
            <h1 style={styles.heading}>
              Cable ERP Platform
            </h1>

            <p style={styles.subText}>
              Smart manufacturing,
              inventory, quotation,
              costing and production
              management for modern cable
              industries.
            </p>

            {/* SLIDER DOTS */}
            <div style={styles.dotsContainer}>
              {backgroundImages.map(
                (_, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.dot,
                      background:
                        currentImage ===
                        index
                          ? "#ffffff"
                          : "rgba(255,255,255,0.4)",
                      transform:
                        currentImage ===
                        index
                          ? "scale(1.3)"
                          : "scale(1)",
                    }}
                  />
                )
              )}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div style={styles.rightPanel}>
          <div style={styles.loginCard}>
            {/* LOGOS */}
            <div style={styles.logoContainer}>
              <img
                src={logo}
                alt="CableNXT Logo"
                style={styles.logo}
              />

              <div
                style={styles.logoDivider}
              ></div>

              <img
                src={clientLogo}
                alt="Client Logo"
                style={styles.clientLogo}
              />
            </div>

            <h2 style={styles.loginTitle}>
              Welcome Back
            </h2>

            <p style={styles.loginSubtitle}>
              Sign in to continue to your
              workspace
            </p>

            {/* USER ID */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                User ID
              </label>

              <input
                type="text"
                placeholder="Enter your user ID"
                value={loginId}
                onChange={(e) =>
                  setLoginId(e.target.value)
                }
                style={styles.input}
              />
            </div>

            {/* PASSWORD */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                Password
              </label>

              <PasswordInput
                placeholder="Enter your password"
                value={password}
                onChange={(e) =>
                  setPassword(
                    e.target.value
                  )
                }
              />

              <p
                style={styles.forgotLink}
                onClick={() => {
                  setForgotUserId(loginId || "");
                  setShowForgotModal(true);
                }}
              >
                Forgot password?
              </p>
            </div>

            {/* LOGIN BUTTON */}
            <button
              onClick={handleLogin}
              disabled={loading}
              style={styles.button}
            >
              {loading
                ? "Signing In..."
                : "Login"}
            </button>

            <p style={styles.footerText}>
              © 2026 CableNXT ERP Platform
            </p>
          </div>
        </div>
      </div>

      {/* FORGOT PASSWORD MODAL */}
      {showForgotModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <button
              style={styles.modalClose}
              onClick={closeForgotModal}
              aria-label="Close"
            >
              ×
            </button>

            {/* STEP 1: ENTER USER ID */}
            {forgotStep === "userId" && (
              <>
                <h3 style={styles.modalTitle}>
                  Forgot Password
                </h3>
                <p style={styles.modalSubtitle}>
                  Enter your User ID and we'll
                  send an OTP to the email
                  registered on your account.
                </p>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    User ID
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your User ID"
                    value={forgotUserId}
                    onChange={(e) =>
                      setForgotUserId(
                        e.target.value
                      )
                    }
                    style={styles.input}
                  />
                </div>

                {forgotError && (
                  <p style={styles.errorText}>
                    {forgotError}
                  </p>
                )}

                <button
                  onClick={handleSendOtp}
                  disabled={forgotLoading}
                  style={styles.button}
                >
                  {forgotLoading
                    ? "Sending OTP..."
                    : "Send OTP"}
                </button>
              </>
            )}

            {/* STEP 2: ENTER OTP */}
            {forgotStep === "otp" && (
              <>
                <h3 style={styles.modalTitle}>
                  Enter OTP
                </h3>
                <p style={styles.modalSubtitle}>
                  We've sent a one-time password
                  to the email registered for
                  User ID <b>{forgotUserId}</b>.
                </p>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    OTP
                  </label>
                  <input
                    type="text"
                    placeholder="Enter the OTP"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value)
                    }
                    style={styles.input}
                  />
                </div>

                {forgotError && (
                  <p style={styles.errorText}>
                    {forgotError}
                  </p>
                )}

                <button
                  onClick={handleVerifyOtp}
                  disabled={forgotLoading}
                  style={styles.button}
                >
                  {forgotLoading
                    ? "Verifying..."
                    : "Verify OTP"}
                </button>

                <p
                  style={styles.resendLink}
                  onClick={handleSendOtp}
                >
                  Didn't get the code? Resend OTP
                </p>
              </>
            )}

            {/* STEP 3: SET NEW PASSWORD */}
            {forgotStep === "reset" && (
              <>
                <h3 style={styles.modalTitle}>
                  Set New Password
                </h3>
                <p style={styles.modalSubtitle}>
                  Choose a new password for your
                  account.
                </p>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    New Password
                  </label>
                  <PasswordInput
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) =>
                      setNewPassword(
                        e.target.value
                      )
                    }
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Confirm Password
                  </label>
                  <PasswordInput
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={(e) =>
                      setConfirmPassword(
                        e.target.value
                      )
                    }
                  />
                </div>

                {forgotError && (
                  <p style={styles.errorText}>
                    {forgotError}
                  </p>
                )}

                <button
                  onClick={handleResetPassword}
                  disabled={forgotLoading}
                  style={styles.button}
                >
                  {forgotLoading
                    ? "Updating..."
                    : "Reset Password"}
                </button>
              </>
            )}

            {/* STEP 4: SUCCESS */}
            {forgotStep === "done" && (
              <>
                <h3 style={styles.modalTitle}>
                  Password Updated
                </h3>
                <p style={styles.modalSubtitle}>
                  Your password has been reset
                  successfully. You can now sign
                  in with your new password.
                </p>

                <button
                  onClick={closeForgotModal}
                  style={styles.button}
                >
                  Back to Login
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  page: {
    display: "flex",
    height: "100vh",
    width: "100%",
    overflow: "hidden",
    fontFamily:
      "'Poppins', sans-serif",
    background: "#f4f7fb",
  },

  /* LEFT PANEL */
  leftPanel: {
    flex: 1,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    transition:
      "background-image 1.2s ease-in-out",
  },

  overlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to right, rgba(2,23,63,0.88), rgba(3,27,78,0.65))",
  },

  leftContent: {
    position: "relative",
    zIndex: 2,
    textAlign: "center",
    color: "#ffffff",
    maxWidth: "560px",
    padding: "40px",
  },

  heading: {
    fontSize: "52px",
    fontWeight: "700",
    marginBottom: "20px",
    letterSpacing: "1px",
    textShadow:
      "0 5px 20px rgba(0,0,0,0.4)",
  },

  subText: {
    fontSize: "17px",
    lineHeight: "1.9",
    opacity: 0.95,
    textShadow:
      "0 2px 12px rgba(0,0,0,0.35)",
  },

  /* DOTS */
  dotsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginTop: "40px",
  },

  dot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    transition: "0.3s ease",
  },

  /* RIGHT PANEL */
  rightPanel: {
    width: "480px",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px",
    boxShadow:
      "-10px 0 40px rgba(2,23,63,0.12)",
  },

  loginCard: {
    width: "100%",
    maxWidth: "360px",
  },

  /* LOGO SECTION */
  logoContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "18px",
    marginBottom: "30px",
  },

  logoDivider: {
    width: "1px",
    height: "50px",
    background: "#d1d5db",
  },

  logo: {
    width: "130px",
    objectFit: "contain",
  },

  clientLogo: {
    width: "120px",
    objectFit: "contain",
  },

  loginTitle: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#02173f",
    marginBottom: "8px",
    textAlign: "center",
  },

  loginSubtitle: {
    color: "#6b7280",
    fontSize: "14px",
    marginBottom: "35px",
    lineHeight: "1.7",
    textAlign: "center",
  },

  inputGroup: {
    marginBottom: "22px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#374151",
  },

  input: {
    width: "100%",
    padding: "15px 16px",
    borderRadius: "12px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    outline: "none",
    background: "#f9fafb",
    transition: "0.25s",
    boxSizing: "border-box",
  },

  eyeButton: {
    position: "absolute",
    top: "50%",
    right: "12px",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    padding: "4px",
    cursor: "pointer",
    color: "#6b7280",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  button: {
    width: "100%",
    padding: "15px",
    background:
      "linear-gradient(to right, #02173f, #1677ff)",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "10px",
    boxShadow:
      "0 10px 25px rgba(22,119,255,0.25)",
    transition: "0.3s",
  },

  footerText: {
    textAlign: "center",
    marginTop: "28px",
    color: "#9ca3af",
    fontSize: "12px",
  },

  /* FORGOT PASSWORD LINK */
  forgotLink: {
    textAlign: "right",
    marginTop: "8px",
    marginBottom: "0",
    fontSize: "13px",
    fontWeight: "600",
    color: "#1677ff",
    cursor: "pointer",
  },

  resendLink: {
    textAlign: "center",
    marginTop: "16px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#1677ff",
    cursor: "pointer",
  },

  errorText: {
    color: "#dc2626",
    fontSize: "13px",
    marginBottom: "16px",
    marginTop: "-8px",
  },

  /* FORGOT PASSWORD MODAL */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,23,63,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    padding: "20px",
  },

  modalCard: {
    position: "relative",
    width: "100%",
    maxWidth: "380px",
    background: "#ffffff",
    borderRadius: "16px",
    padding: "36px 32px",
    boxShadow: "0 20px 60px rgba(2,23,63,0.3)",
    fontFamily: "'Poppins', sans-serif",
  },

  modalClose: {
    position: "absolute",
    top: "14px",
    right: "16px",
    background: "transparent",
    border: "none",
    fontSize: "22px",
    lineHeight: "1",
    color: "#9ca3af",
    cursor: "pointer",
  },

  modalTitle: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#02173f",
    marginBottom: "8px",
  },

  modalSubtitle: {
    color: "#6b7280",
    fontSize: "13px",
    marginBottom: "24px",
    lineHeight: "1.6",
  },
};

export default Login;